import { create } from 'zustand';
import { z } from 'zod';
import { defaultColors, type ColorSet } from '@/config/themes';
import { modes, type ModeId } from '@/config/modes.config';
import { levels, type LevelId } from '@/config/levels.config';
import { LOCALES, detectLocale, type Locale } from '@/i18n';
import i18n from '@/i18n';
import { readJSON, writeJSON } from '@/lib/storage';
import { SOUND_THEMES, type SoundTheme } from '@/lib/sound';
import type { Blockers } from '@/engine/combos';

const KEY = 'cc.settings.v1';
const DEFAULT_BLOCKERS: Blockers = { rows: 1, cols: 1 };
export const subKey = (mode: ModeId, level: LevelId): string => `${mode}:${level}`;

const hex = z.string().regex(/^#[0-9a-f]{6}$/i);
const ColorsSchema = z.object({
  accent: hex,
  a: hex,
  b: hex,
  mfill: hex,
  mcell: hex,
  combo: hex,
  blocker: hex,
});
const SettingsSchema = z.object({
  lang: z.enum(LOCALES),
  colors: ColorsSchema,
  /** chosen difficulty index per `${modeId}:${levelId}` */
  subs: z.record(z.string(), z.number().int().min(0)),
  sound: z.boolean(),
  /** show the A (combos) and B (hands) numbers under each block; the signs always stay */
  showA: z.boolean(),
  showB: z.boolean(),
  /** lighting order on the play screen: by diagonals, from the far corner, or a random direction per question */
  fillDiagonal: z.boolean(),
  fillReversed: z.boolean(),
  fillRandom: z.boolean(),
  /** Card Blockers per `${modeId}:${levelId}`: rows and columns of the combo grid our cards block (0–3 each or 'random', ≥ 1 line) */
  cardBlockers: z.record(
    z.string(),
    z.object({
      rows: z.union([z.number().int().min(0).max(3), z.literal('random')]),
      cols: z.union([z.number().int().min(0).max(3), z.literal('random')]),
    }),
  ),
  soundTheme: z.enum(SOUND_THEMES),
  /** neon glow on the combo grid squares */
  glow: z.boolean(),
  /** print each square's two suits on it (play HUD toggle) */
  showSuits: z.boolean(),
  /** colour the suit icons per suit (play HUD toggle) */
  suitColors: z.boolean(),
});
type Settings = z.infer<typeof SettingsSchema>;

const defaults = (): Settings => ({
  lang: detectLocale(),
  colors: { ...defaultColors },
  subs: Object.fromEntries(modes.flatMap((m) => levels.map((l) => [subKey(m.id, l.id), 0]))),
  sound: false,
  showA: true,
  showB: true,
  fillDiagonal: false,
  fillReversed: false,
  fillRandom: false,
  cardBlockers: {},
  soundTheme: 'calm',
  glow: true,
  showSuits: false,
  suitColors: false,
});

function load(): Settings {
  // colours partial too, so a set stored before a colour was added still loads (defaults fill the gaps)
  const stored = readJSON(
    KEY,
    SettingsSchema.partial().extend({
      colors: ColorsSchema.partial(),
      cardBlockers: SettingsSchema.shape.cardBlockers.catch({}),
    }),
  );
  const d = defaults();
  return {
    lang: stored?.lang ?? d.lang,
    colors: {
      ...d.colors,
      ...Object.fromEntries(
        Object.entries(stored?.colors ?? {}).filter(([, v]) => v !== undefined),
      ),
    },
    subs: { ...d.subs, ...(stored?.subs ?? {}) },
    sound: stored?.sound ?? d.sound,
    showA: stored?.showA ?? d.showA,
    showB: stored?.showB ?? d.showB,
    fillDiagonal: stored?.fillDiagonal ?? d.fillDiagonal,
    fillReversed: stored?.fillReversed ?? d.fillReversed,
    fillRandom: stored?.fillRandom ?? d.fillRandom,
    cardBlockers: stored?.cardBlockers ?? d.cardBlockers,
    soundTheme: stored?.soundTheme ?? d.soundTheme,
    glow: stored?.glow ?? d.glow,
    showSuits: stored?.showSuits ?? d.showSuits,
    suitColors: stored?.suitColors ?? d.suitColors,
  };
}

interface SettingsState extends Settings {
  setLang: (lang: Locale) => void;
  setColor: (key: keyof ColorSet, value: string) => void;
  setColors: (colors: ColorSet) => void;
  setSub: (mode: ModeId, level: LevelId, index: number) => void;
  setSound: (on: boolean) => void;
  setShowA: (on: boolean) => void;
  setShowB: (on: boolean) => void;
  setFillDiagonal: (on: boolean) => void;
  setFillReversed: (on: boolean) => void;
  setFillRandom: (on: boolean) => void;
  /** the lines chosen for a level (default one row + one column) */
  blockersFor: (mode: ModeId, level: LevelId) => Blockers;
  /** ignored when it would leave no blocked line at all */
  setCardBlockers: (mode: ModeId, level: LevelId, next: Partial<Blockers>) => void;
  setSoundTheme: (theme: SoundTheme) => void;
  setGlow: (on: boolean) => void;
  setShowSuits: (on: boolean) => void;
  setSuitColors: (on: boolean) => void;
  subIndex: (mode: ModeId, level: LevelId) => number;
  bMaxFor: (mode: ModeId, level: LevelId) => number;
  reset: () => void;
}

/** Push user colours into CSS custom properties on <html>; everything else cascades. */
export function applyColors(c: ColorSet): void {
  const s = document.documentElement.style;
  s.setProperty('--accent', c.accent);
  s.setProperty('--color-a', c.a);
  s.setProperty('--color-b', c.b);
  s.setProperty('--m-fill', c.mfill);
  s.setProperty('--m-cell', c.mcell);
  s.setProperty('--combo', c.combo);
  s.setProperty('--blocker', c.blocker);
}

export const useSettings = create<SettingsState>((set, get) => ({
  ...load(),
  setLang: (lang) => set({ lang }),
  setColor: (key, value) => set((s) => ({ colors: { ...s.colors, [key]: value } })),
  setColors: (colors) => set({ colors: { ...colors } }),
  setSub: (mode, level, index) =>
    set((s) => ({ subs: { ...s.subs, [subKey(mode, level)]: index } })),
  setSound: (sound) => set({ sound }),
  setShowA: (showA) => set({ showA }),
  setShowB: (showB) => set({ showB }),
  setFillDiagonal: (fillDiagonal) => set({ fillDiagonal }),
  setFillReversed: (fillReversed) => set({ fillReversed }),
  setFillRandom: (fillRandom) => set({ fillRandom }),
  blockersFor: (mode, level) => get().cardBlockers[subKey(mode, level)] ?? DEFAULT_BLOCKERS,
  setCardBlockers: (mode, level, next) =>
    set((s) => {
      const k = subKey(mode, level);
      const b = { ...(s.cardBlockers[k] ?? DEFAULT_BLOCKERS), ...next };
      // a random draw never leaves no line, so only a fixed 0 + 0 is refused
      return b.rows === 0 && b.cols === 0 ? {} : { cardBlockers: { ...s.cardBlockers, [k]: b } };
    }),
  setSoundTheme: (soundTheme) => set({ soundTheme }),
  setGlow: (glow) => set({ glow }),
  setShowSuits: (showSuits) => set({ showSuits }),
  setSuitColors: (suitColors) => set({ suitColors }),
  subIndex: (mode, level) => {
    const caps = modes.find((m) => m.id === mode)?.caps ?? [];
    return Math.min(get().subs[subKey(mode, level)] ?? 0, Math.max(0, caps.length - 1));
  },
  bMaxFor: (mode, level) => {
    const m = modes.find((x) => x.id === mode);
    return m?.caps[get().subIndex(mode, level)] ?? m?.b.max ?? 0;
  },
  reset: () => set({ colors: { ...defaultColors }, subs: defaults().subs }),
}));

// Side effects: persist, sync i18n + <html lang>, and apply colours.
function sync(s: Settings): void {
  writeJSON(KEY, {
    lang: s.lang,
    colors: s.colors,
    subs: s.subs,
    sound: s.sound,
    showA: s.showA,
    showB: s.showB,
    fillDiagonal: s.fillDiagonal,
    fillReversed: s.fillReversed,
    fillRandom: s.fillRandom,
    cardBlockers: s.cardBlockers,
    soundTheme: s.soundTheme,
    glow: s.glow,
    showSuits: s.showSuits,
    suitColors: s.suitColors,
  });
  if (i18n.language !== s.lang) void i18n.changeLanguage(s.lang);
  document.documentElement.lang = s.lang;
  applyColors(s.colors);
  document.documentElement.toggleAttribute('data-glow', s.glow);
  document.documentElement.toggleAttribute('data-suits', s.showSuits);
  document.documentElement.toggleAttribute('data-suit-colors', s.suitColors);
}
useSettings.subscribe(sync);
sync(useSettings.getState());
